import React, { useState, useEffect } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Box,
  Chip,
  TablePagination,
  IconButton,
  Grid,
  TextField,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import axios from 'axios';
import baseURL from '../../../baseURL';
import MasterPageShell, {
  masterPaperSx,
  masterListPaperSx,
  masterSectionTitleSx,
  masterPrimaryBtnSx,
  masterFieldSx,
  masterTableHeadCellSx,
} from '../../common/MasterPageShell';

const emptyForm = {
  _id: '',
  interestRateNumeric: '',
  interestRateWords: '',
  durationNumeric: '',
  durationWords: '',
};

const GovernmentInterestsListPage = () => {
  const [interests, setInterests] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedInterest, setSelectedInterest] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGovernmentInterests();
  }, []);

  const fetchGovernmentInterests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${baseURL}/government-interests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInterests(response.data.data || []);
    } catch (error) {
      setSnackbarMessage('Failed to fetch government interests');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setIsEditing(false);
    setForm(emptyForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        interestRateNumeric: form.interestRateNumeric,
        interestRateWords: form.interestRateWords,
        durationNumeric: form.durationNumeric,
        durationWords: form.durationWords,
      };
      if (isEditing) {
        await axios.put(`${baseURL}/government-interests/${form._id}`, payload, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        setSnackbarMessage('Government interest updated');
      } else {
        await axios.post(`${baseURL}/government-interests`, payload, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        setSnackbarMessage('Government interest created');
      }
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      resetForm();
      fetchGovernmentInterests();
    } catch (error) {
      setSnackbarMessage(error.response?.data?.message || 'Failed to save government interest');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (interest) => {
    setIsEditing(true);
    setForm({
      _id: interest._id,
      interestRateNumeric: interest.interestRateNumeric ?? '',
      interestRateWords: interest.interestRateWords ?? '',
      durationNumeric: interest.durationNumeric ?? '',
      durationWords: interest.durationWords ?? '',
    });
  };

  const handleDeleteInterest = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${baseURL}/government-interests/${selectedInterest._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSnackbarMessage('Government interest deleted');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      fetchGovernmentInterests();
      setOpenDeleteDialog(false);
    } catch (error) {
      setSnackbarMessage(error.response?.data?.message || 'Error deleting government interest');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  return (
    <MasterPageShell title="Government Interests" subtitle="Master · Rates" loading={loading}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={masterPaperSx}>
            <Typography sx={masterSectionTitleSx}>
              {isEditing ? 'Edit rate' : 'Create rate'}
            </Typography>
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                label="Interest rate (numeric)"
                name="interestRateNumeric"
                type="number"
                value={form.interestRateNumeric}
                onChange={handleChange}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                sx={masterFieldSx}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="Interest rate (words)"
                name="interestRateWords"
                value={form.interestRateWords}
                onChange={handleChange}
                sx={masterFieldSx}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="Duration (numeric)"
                name="durationNumeric"
                type="number"
                value={form.durationNumeric}
                onChange={handleChange}
                inputProps={{ min: 0 }}
                sx={masterFieldSx}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="Duration (words)"
                name="durationWords"
                value={form.durationWords}
                onChange={handleChange}
                sx={masterFieldSx}
              />
              <Button type="submit" fullWidth variant="contained" sx={masterPrimaryBtnSx} disabled={saving}>
                {saving ? 'Saving…' : isEditing ? 'Update rate' : 'Create rate'}
              </Button>
              {isEditing ? (
                <Button fullWidth variant="outlined" onClick={resetForm} sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 650 }}>
                  Cancel
                </Button>
              ) : null}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={masterListPaperSx}>
            <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ ...masterSectionTitleSx, mb: 0 }}>Rates list</Typography>
              <Chip size="small" label={`${interests.length} items`} sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }} />
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={masterTableHeadCellSx}>Rate</TableCell>
                    <TableCell sx={masterTableHeadCellSx}>Rate (words)</TableCell>
                    <TableCell sx={masterTableHeadCellSx}>Duration</TableCell>
                    <TableCell sx={masterTableHeadCellSx}>Duration (words)</TableCell>
                    <TableCell align="right" sx={masterTableHeadCellSx}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(rowsPerPage > 0
                    ? interests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    : interests
                  ).map((interest) => (
                    <TableRow key={interest._id} hover>
                      <TableCell>
                        <Chip label={`${interest.interestRateNumeric}%`} size="small" sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }} />
                      </TableCell>
                      <TableCell>{interest.interestRateWords}</TableCell>
                      <TableCell>
                        <Chip label={interest.durationNumeric} size="small" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }} />
                      </TableCell>
                      <TableCell>{interest.durationWords}</TableCell>
                      <TableCell align="right">
                        <IconButton color="primary" size="small" onClick={() => handleEdit(interest)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => {
                            setSelectedInterest(interest);
                            setOpenDeleteDialog(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {interests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#94a3b8' }}>
                        No government interests yet
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={interests.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 750 }}>Delete government interest</DialogTitle>
        <DialogContent>
          <Typography>
            Delete rate {selectedInterest?.interestRateNumeric}%? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setOpenDeleteDialog(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDeleteInterest} sx={{ textTransform: 'none', borderRadius: 2 }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%', borderRadius: 2 }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </MasterPageShell>
  );
};

export default GovernmentInterestsListPage;
